// src/pages/Messages.jsx
import React, { useMemo, useRef } from "react";
import { useUser } from "../contexts/UserContext";
import { useMessages } from "../contexts/MessagesContext";
import SpinnerOverlay from "../components/ui/SpinnerOverlay";
import AdminMessagesHeader from "../components/ui/AdminMessagesHeader";

/* ===================== Icons ===================== */
const SearchIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
  </svg>
);

const RefreshIcon = ({ spinning }) => (
  <svg className={`w-4 h-4 ${spinning ? "animate-spin" : ""}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
      d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
  </svg>
);

const SendIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
  </svg>
);

const ClockIcon = () => (
  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

const CheckCircleIcon = ({ className = "w-3.5 h-3.5" }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

const MailIcon = ({ className = "w-4 h-4" }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
      d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
  </svg>
);

const PhoneIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
      d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
  </svg>
);

const UserIcon = ({ className = "w-4 h-4" }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
      d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
  </svg>
);

const MessageSquareIcon = ({ className = "w-4 h-4" }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
      d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
  </svg>
);

const CalendarIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
      d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
  </svg>
);

const InboxIcon = ({ className = "w-6 h-6" }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
      d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
  </svg>
);

const ReplyIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
  </svg>
);

const XIcon = ({ className = "w-5 h-5" }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
  </svg>
);

const SparkleIcon = () => (
  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
    <path d="M12 0L14.59 8.41L23 11L14.59 13.59L12 22L9.41 13.59L1 11L9.41 8.41L12 0Z" />
  </svg>
);

/* ===================== UI Helpers ===================== */
const cls = (...a) => a.filter(Boolean).join(" ");

const fmt = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso);
  const now  = new Date();
  const diff = now - d;
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  if (days === 0) {
    const hours = Math.floor(diff / (1000 * 60 * 60));
    if (hours === 0) {
      const mins = Math.max(0, Math.floor(diff / (1000 * 60)));
      return `לפני ${mins} דקות`;
    }
    return `לפני ${hours} שעות`;
  }
  if (days === 1) return "אתמול";
  if (days < 7)   return `לפני ${days} ימים`;
  return d.toLocaleDateString("he-IL");
};

const initials = (first, last) => {
  const s = `${(first || "").trim()} ${(last || "").trim()}`.trim();
  if (!s) return "?";
  return s.split(/\s+/).slice(0, 2).map((p) => (p[0] || "").toUpperCase()).join("");
};

const StatusBadge = ({ replied }) => (
  <div className={cls(
    "inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-xs font-bold",
    "backdrop-blur-md transition-all duration-300 border",
    replied
      ? "bg-gradient-to-r from-emerald-500/10 to-teal-500/10 text-emerald-700 border-emerald-200/50"
      : "bg-gradient-to-r from-amber-500/10 to-orange-500/10 text-amber-700 border-amber-200/50 animate-pulse"
  )}>
    {replied ? (
      <><CheckCircleIcon /><span>נענה</span></>
    ) : (
      <><ClockIcon /><span>ממתין</span></>
    )}
  </div>
);

const KpiCard = ({ title, value, hint, icon, gradient, iconBgClass }) => (
  <div className={cls("p-3 sm:p-4 rounded-xl border", gradient)}>
    <div className="flex items-center gap-2 sm:gap-3">
      <div className={cls(
        "w-9 h-9 sm:w-10 sm:h-10 rounded-lg flex items-center justify-center shadow-sm flex-shrink-0",
        iconBgClass || "bg-gradient-to-br from-gray-600 to-gray-800"
      )}>
        {icon}
      </div>
      <div className="min-w-0">
        <p className="text-xs sm:text-sm text-gray-700">{title}</p>
        <p className="text-xl sm:text-2xl font-bold text-gray-900">{value}</p>
        {hint && <p className="text-xs text-gray-500 mt-0.5">{hint}</p>}
      </div>
    </div>
  </div>
);

/* ===================== Page ===================== */
export default function Messages() {
  const { role } = useUser();
  const isAdmin = role === "admin";

  const {
    page, status, q,
    setQ, setStatus, setPage,
    filtered, total, totalPages, counts,
    loading, detailsLoading, sendingReply, refreshing,
    selected, openMessage, clearSelected,
    replyText, setReplyText,
    showReplyPanel, setShowReplyPanel,
    sendReply, canReply,
    refresh, goNext, goPrev,
  } = useMessages();

  // Spaces in admin_reply should NOT count as replied
  const hasAdminReply = useMemo(() => {
    return !!(selected?.admin_reply && String(selected.admin_reply).trim() !== "");
  }, [selected]);

  // Used for mobile scroll-to-details
  const detailsPanelRef = useRef(null);

  const handleOpenMessage = (m) => {
    openMessage(m);
    setTimeout(() => {
      detailsPanelRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 80);
  };

  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-indigo-50 p-4 sm:p-6" dir="rtl">
        <div className="max-w-md mx-auto">
          <div className="bg-white/80 backdrop-blur-xl rounded-3xl shadow-2xl p-8 text-center border border-red-100">
            <div className="w-24 h-24 mx-auto mb-6 bg-gradient-to-br from-red-500 to-pink-500 rounded-3xl flex items-center justify-center shadow-lg shadow-red-200/50">
              <XIcon className="w-12 h-12 text-white" />
            </div>
            <h2 className="text-2xl font-black text-gray-900 mb-2">אין הרשאה</h2>
            <p className="text-gray-600">עמוד זה זמין למנהל מערכת בלבד</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <AdminMessagesHeader
        title="מרכז הודעות"
        subtitle="ניהול הודעות משתמשים: צפייה, חיפוש, מענה וסינון סטטוסים"
        unrepliedCount={Number(counts?.globalUnreplied || 0)}
      />

      <div className="min-h-screen max-w-7xl mx-auto p-4 sm:p-6" dir="rtl">
        {(loading || detailsLoading || sendingReply) && (
          <SpinnerOverlay variant="glow" text="טוען..." dark={false} blur={false} />
        )}

        {/* ── Filters ── */}
        <div className="bg-white rounded-2xl border border-gray-200 p-4 sm:p-6 md:p-8 mb-6">
          <div className="flex flex-col sm:flex-row sm:items-center gap-3">

            {/* Search — grows */}
            <div className="flex-1 min-w-0">
              <div className="relative">
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none">
                  <SearchIcon />
                </span>
                <input
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="חיפוש..."
                  className="w-full px-4 pr-10 py-2 rounded-lg border border-gray-200 bg-white outline-none transition-all focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400"
                />
              </div>
            </div>

            {/* Status select — full width on xs, auto on sm+ */}
            <select
              value={status}
              onChange={(e) => { setStatus(e.target.value); setPage(1); }}
              className="w-full sm:w-auto px-4 py-2 rounded-lg border border-gray-200 bg-white outline-none transition-all focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400 font-semibold text-gray-700"
            >
              <option value="all">כל ההודעות</option>
              <option value="open">ללא מענה</option>
              <option value="replied">נענו</option>
            </select>

            {/* Refresh */}
            <button
              onClick={refresh}
              disabled={refreshing}
              className="w-full sm:w-auto px-4 py-2 rounded-lg text-sm font-semibold bg-white border border-gray-200 hover:bg-gray-50 disabled:opacity-50 transition-all flex items-center justify-center gap-2"
            >
              <RefreshIcon spinning={refreshing} />
              {refreshing ? "מרענן..." : "רענן"}
            </button>
          </div>
        </div>

        {/* ── KPI Cards ── */}
        {/* 2 cols on xs, 4 cols on sm+ */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4 mb-6">
          <KpiCard
            title="סה״כ הודעות"
            value={counts.globalTotal}
            gradient="bg-gradient-to-r from-gray-50 to-gray-100 border-gray-200"
            iconBgClass="bg-gradient-to-br from-indigo-500 to-purple-600"
            icon={<MessageSquareIcon className="w-5 h-5 text-white" />}
          />
          <KpiCard
            title="ממתינות"
            value={counts.globalUnreplied}
            hint={counts.globalUnreplied ? "🔥 דורש טיפול" : "✨ הכל מטופל"}
            gradient="bg-gradient-to-r from-amber-50 to-orange-50 border-amber-200"
            iconBgClass="bg-gradient-to-br from-amber-500 to-orange-500"
            icon={<span className="text-white font-black">⏳</span>}
          />
          <KpiCard
            title="נענו"
            value={counts.globalReplied}
            gradient="bg-gradient-to-r from-emerald-50 to-green-50 border-emerald-200"
            iconBgClass="bg-gradient-to-br from-emerald-500 to-green-500"
            icon={<CheckCircleIcon className="w-5 h-5 text-white" />}
          />
          <KpiCard
            title="לא נקראו"
            value={counts.globalUnread}
            gradient="bg-gradient-to-r from-purple-50 to-pink-50 border-purple-200"
            iconBgClass="bg-gradient-to-br from-purple-500 to-pink-500"
            icon={<SparkleIcon className="w-5 h-5 text-white" />}
          />
        </div>

        {/* ── Main two-panel grid ──
            Stacks on mobile (list first, details below), side-by-side at lg.
        */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

          {/* ── Messages list ── */}
          <div>
            <div className="bg-white/80 backdrop-blur-sm rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
              {/* Panel header */}
              <div className="px-4 sm:px-6 py-4 bg-gradient-to-r from-gray-50 to-gray-100 border-b border-gray-200">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1">
                  <h2 className="text-base font-bold text-gray-900">רשימת הודעות</h2>
                  <div className="text-sm text-gray-600 font-medium">
                    עמוד {page} מתוך {totalPages} • {filtered.length} הודעות
                  </div>
                </div>
              </div>

              {/* List body — shorter on mobile to keep details panel accessible */}
              <div className="max-h-[360px] sm:max-h-[480px] lg:max-h-[620px] overflow-y-auto divide-y divide-gray-100">
                {filtered.length === 0 ? (
                  <div className="p-12 text-center">
                    <div className="w-16 h-16 sm:w-20 sm:h-20 bg-gradient-to-br from-indigo-100 to-purple-100 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-sm">
                      <InboxIcon className="w-8 h-8 sm:w-10 sm:h-10 text-indigo-500" />
                    </div>
                    <p className="text-gray-800 font-bold text-base sm:text-lg">
                      {loading ? "טוען הודעות..." : "אין הודעות להצגה"}
                    </p>
                    <p className="text-gray-500 text-sm mt-2">נסה לשנות את הסינון או החיפוש</p>
                  </div>
                ) : (
                  filtered.map((m) => {
                    const replied  = !!(m.admin_reply && String(m.admin_reply).trim() !== "");
                    const unread   = String(m.is_read ?? 0) === "0";
                    const isActive = selected && String(selected.id) === String(m.id);

                    return (
                      <div
                        key={m.id}
                        className={cls(
                          "cursor-pointer transition-colors",
                          isActive ? "bg-indigo-50/60 hover:bg-indigo-50/80" : "hover:bg-gray-50"
                        )}
                        onClick={() => handleOpenMessage(m)}
                      >
                        <div className="p-4 sm:p-5">
                          <div className="flex items-start gap-3 sm:gap-4">
                            {/* Avatar */}
                            <div className="flex-shrink-0">
                              <div className={cls(
                                "w-10 h-10 sm:w-12 sm:h-12 rounded-xl flex items-center justify-center font-bold text-white shadow-sm",
                                unread
                                  ? "bg-gradient-to-br from-amber-500 to-orange-500"
                                  : replied
                                  ? "bg-gradient-to-br from-emerald-500 to-teal-500"
                                  : "bg-gradient-to-br from-indigo-500 to-purple-500"
                              )}>
                                <span className="text-base sm:text-lg">{initials(m.first_name, m.last_name)}</span>
                              </div>
                            </div>

                            {/* Content */}
                            <div className="flex-1 min-w-0">
                              <div className="flex items-start justify-between gap-2 mb-1.5">
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2 flex-wrap mb-1">
                                    <span className={cls("font-bold text-sm sm:text-base", isActive ? "text-indigo-900" : "text-gray-900")}>
                                      {[m.first_name, m.last_name].filter(Boolean).join(" ") || "אנונימי"}
                                    </span>
                                    {unread && (
                                      <span className="px-2 py-0.5 bg-red-500 text-white text-xs rounded-full font-bold">חדש</span>
                                    )}
                                  </div>

                                  <p className="text-xs sm:text-sm text-gray-600 font-medium line-clamp-1 mb-1.5">
                                    {m.subject || "ללא נושא"}
                                  </p>

                                  <div className="flex items-center gap-1.5 sm:gap-2 flex-wrap text-xs sm:text-sm">
                                    <span className="text-gray-600 truncate max-w-[140px] sm:max-w-none">{m.email}</span>
                                    <span className="text-gray-300">•</span>
                                    <span className="text-xs text-gray-500 font-medium">{fmt(m.created_at)}</span>
                                  </div>
                                </div>

                                <div className="flex-shrink-0">
                                  <StatusBadge replied={replied} />
                                </div>
                              </div>

                              <p className="text-xs sm:text-sm text-gray-600 line-clamp-2 leading-relaxed">
                                {m.message}
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>

              {/* Pagination */}
              <div className="px-4 sm:px-6 py-4 bg-gradient-to-r from-gray-50 to-gray-100 border-t border-gray-200">
                <div className="flex items-center justify-between gap-2">
                  
                  <button
                    disabled={page >= totalPages}
                    onClick={goNext}
                    className={cls(
                      "px-3 sm:px-4 py-2 rounded-lg text-sm font-semibold transition-all",
                      page >= totalPages
                        ? "bg-white border border-gray-200 text-gray-400 cursor-not-allowed opacity-60"
                        : "bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 hover:border-gray-400"
                    )}
                  >
                   → הבא 
                  </button>

                  <div className="text-sm text-gray-700 font-semibold">
                    <span className="text-gray-900">{page}</span>
                    <span className="text-gray-400 mx-1">/</span>
                    <span className="text-gray-900">{totalPages}</span>
                  </div>

                  <button
                    disabled={page <= 1}
                    onClick={goPrev}
                    className={cls(
                      "px-3 sm:px-4 py-2 rounded-lg text-sm font-semibold transition-all",
                      page <= 1
                        ? "bg-white border border-gray-200 text-gray-400 cursor-not-allowed opacity-60"
                        : "bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 hover:border-gray-400"
                    )}
                  >
                     הקודם ←
                  </button>

                </div>
              </div>
            </div>
          </div>

          {/* ── Details panel ── */}
          <div ref={detailsPanelRef}>
            <div className="bg-white/80 backdrop-blur-sm rounded-2xl border border-gray-200 shadow-sm overflow-hidden lg:sticky lg:top-6">
              {!selected ? (
                <div className="p-8">
                  <div className="text-center py-8 sm:py-12">
                    <div className="w-16 h-16 sm:w-20 sm:h-20 bg-gradient-to-br from-indigo-100 to-purple-100 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-sm">
                      <MailIcon className="w-8 h-8 sm:w-10 sm:h-10 text-indigo-500" />
                    </div>
                    <h3 className="text-lg font-bold text-gray-900 mb-2">בחר הודעה לצפייה</h3>
                    <p className="text-gray-600 text-sm">לחץ על הודעה מהרשימה כדי לצפות בפרטים</p>
                  </div>
                </div>
              ) : (
                <div className="p-4 sm:p-6 space-y-4 sm:space-y-5">
                  {/* Details header — fixed duplicate items-start/items-center */}
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex flex-wrap gap-2">
                      <StatusBadge replied={hasAdminReply} />
                      {String(selected.is_read ?? 0) === "1" && (
                        <span className="px-2.5 py-1 bg-gray-100 text-gray-600 text-xs rounded-full font-bold">
                          נקרא
                        </span>
                      )}
                    </div>
                    <button
                      onClick={clearSelected}
                      className="p-2 rounded-xl hover:bg-white/80 border border-gray-200 transition-colors flex-shrink-0"
                      title="סגור"
                    >
                      <XIcon />
                    </button>
                  </div>

                  {/* Sender info */}
                  <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-2xl p-3 sm:p-4 border border-indigo-100">
                    <div className="space-y-2.5">
                      <div className="flex items-center gap-2.5">
                        <div className="w-8 h-8 bg-indigo-100 rounded-lg flex items-center justify-center flex-shrink-0">
                          <UserIcon className="text-indigo-600" />
                        </div>
                        <span className="font-semibold text-gray-900 text-sm">
                          {[selected.first_name, selected.last_name].filter(Boolean).join(" ") || "אנונימי"}
                        </span>
                      </div>

                      <div className="flex items-center gap-2.5">
                        <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center flex-shrink-0">
                          <MailIcon className="text-purple-600" />
                        </div>
                        <span className="text-gray-700 text-sm truncate">{selected.email}</span>
                      </div>

                      {selected.phone && (
                        <div className="flex items-center gap-2.5">
                          <div className="w-8 h-8 bg-amber-100 rounded-lg flex items-center justify-center flex-shrink-0">
                            <PhoneIcon className="w-4 h-4 text-amber-700" />
                          </div>
                          <span className="text-gray-700 text-sm">{selected.phone}</span>
                        </div>
                      )}

                      <div className="flex items-center gap-2.5">
                        <div className="w-8 h-8 bg-pink-100 rounded-lg flex items-center justify-center flex-shrink-0">
                          <CalendarIcon className="text-pink-600" />
                        </div>
                        <span className="text-gray-700 text-sm">{fmt(selected.created_at)}</span>
                      </div>
                    </div>
                  </div>

                  {/* Message body
                      break-words prevents long URLs or unspaced Hebrew text from overflowing
                      the container on narrow screens. */}
                  <div>
                    <h4 className="text-sm font-bold text-gray-900 mb-2">תוכן ההודעה</h4>
                    <div className="bg-white rounded-xl p-3 sm:p-4 border border-gray-200">
                      <p className="text-gray-800 whitespace-pre-wrap leading-relaxed break-words text-sm">
                        {selected.message || "אין תוכן"}
                      </p>
                    </div>
                  </div>

                  {/* Reply section */}
                  {hasAdminReply ? (
                    <div>
                      <h4 className="text-sm font-bold text-gray-900 mb-2">תשובת המנהל</h4>
                      <div className="bg-emerald-50 rounded-xl p-3 sm:p-4 border border-emerald-200">
                        <p className="text-gray-800 whitespace-pre-wrap leading-relaxed break-words text-sm">
                          {selected.admin_reply}
                        </p>
                        {selected.reply_sent_at && (
                          <div className="mt-3 flex items-center gap-2 text-sm text-emerald-700 font-bold">
                            <MailIcon className="w-4 h-4" />
                            נשלח במייל ({fmt(selected.reply_sent_at)})
                          </div>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {!showReplyPanel ? (
                        <button
                          onClick={() => setShowReplyPanel(true)}
                          className="w-full py-3 rounded-lg bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-bold hover:from-indigo-700 hover:to-purple-700 transition-all shadow-sm flex items-center justify-center gap-2"
                        >
                          <ReplyIcon />
                          כתוב תשובה
                        </button>
                      ) : (
                        <>
                          <div className="flex items-center justify-between">
                            <h4 className="text-sm font-bold text-gray-900">כתיבת תשובה</h4>
                            <button
                              onClick={() => setShowReplyPanel(false)}
                              className="text-sm font-bold text-gray-500 hover:text-gray-700"
                            >
                              ביטול
                            </button>
                          </div>

                          <textarea
                            value={replyText}
                            onChange={(e) => setReplyText(e.target.value)}
                            placeholder="כתוב את תשובתך כאן..."
                            className="w-full min-h-[120px] sm:min-h-[140px] px-4 py-3 rounded-xl border border-gray-200 bg-white outline-none transition-all focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400 resize-none font-medium text-sm"
                            autoFocus
                          />

                          <div className="flex items-center justify-between text-xs">
                            <span className="text-gray-500 font-medium">התשובה תישלח במייל</span>
                            <span className={cls("font-bold", replyText.trim().length > 4500 ? "text-red-600" : "text-gray-500")}>
                              {replyText.trim().length}/5000
                            </span>
                          </div>

                          <button
                            onClick={sendReply}
                            disabled={
                              sendingReply ||
                              replyText.trim().length < 2 ||
                              replyText.trim().length > 5000 ||
                              !canReply
                            }
                            className="w-full py-3 rounded-lg bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-bold hover:from-indigo-700 hover:to-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-sm flex items-center justify-center gap-2"
                          >
                            <SendIcon />
                            {sendingReply ? "שולח..." : "שלח תשובה"}
                          </button>
                        </>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

        </div>
      </div>
    </>
  );
}